import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  DatePicker,
  Drawer,
  Dropdown,
  Empty,
  Form,
  Image,
  Input,
  Modal,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Upload,
  message,
} from "antd";
import {
  CloseOutlined,
  MessageOutlined,
  MoreOutlined,
  PictureOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  updateDoc,
} from "firebase/firestore";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { auth, db } from "../../firebase";
import {
  isAssignableInspector,
  getUserLabel,
  getDisplayName,
} from "../../helper/userDisplay";
import {
  createPropertyTask,
  taskAssigneeNames,
  needsAssignment,
  hasOpenQuestion,
} from "../../helper/propertyTasks";
import {
  addDiscussionMessage,
  getOrCreateDiscussion,
  markThreadRead,
  subjectThreadId,
  subscribeDiscussionMessages,
} from "../../helper/discussions";
import {
  MessageActionsMenu,
  MessageBody,
  useMessageEdit,
} from "../../components/discussionMessage";
import { uploadFile } from "../../helper/firebaseUpload";
import PageHeader from "../../components/pageHeader/PageHeader";

const COLLECTION = "property_tasks";

const PropertyTasks = () => {
  const [tasks, setTasks] = useState([]);
  const [properties, setProperties] = useState([]);
  const [inspectors, setInspectors] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filterProperty, setFilterProperty] = useState(null);
  const [needsOnly, setNeedsOnly] = useState(false);
  // Edit-task modal (title/description/deadline/assignees on an existing task).
  const [editTask, setEditTask] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const adminUser = useSelector((s) => s.userDataSlice?.userData);
  // Unread discussion-thread ids (from the single layout-level listener, via
  // redux) → red dot on rows with new chatter.
  const unreadThreadIds = useSelector(
    (s) => s.userDataSlice?.unreadThreadIds || [],
  );
  const unreadSet = useMemo(() => new Set(unreadThreadIds), [unreadThreadIds]);
  const [searchParams, setSearchParams] = useSearchParams();
  // Deep-link from a discussion's "View task" link (/tasks?task={id}) — focuses
  // the table on that one task.
  const taskFilter = searchParams.get("task");
  // Questions filter + discussion-thread drawer.
  const [questionsOnly, setQuestionsOnly] = useState(false);
  const [threadTask, setThreadTask] = useState(null);
  const [threadId, setThreadId] = useState(null);
  const [notes, setNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  // Inline edit of your own task-thread message.
  // Shared edit / soft-delete of your own messages (same hook as DiscussionDrawer).
  const noteEdit = useMessageEdit(threadId);
  const [replyText, setReplyText] = useState("");
  const [replyImages, setReplyImages] = useState([]);
  const [uploadingImg, setUploadingImg] = useState(false);
  const [posting, setPosting] = useState(false);
  // Whether a reply was posted while the drawer was open (→ refresh on close so
  // the hasOpenQuestion flag/tag/count update).
  const [threadDirty, setThreadDirty] = useState(false);

  // Deep-link from the dashboard "Questions to answer" widget (/tasks?questions=1).
  useEffect(() => {
    if (searchParams.get("questions") === "1") setQuestionsOnly(true);
  }, [searchParams]);

  // Deep-link from the Properties table's Tasks count (/tasks?propertyId={id}) —
  // pre-select that property in the filter dropdown.
  useEffect(() => {
    const pid = searchParams.get("propertyId");
    if (pid) setFilterProperty(pid);
  }, [searchParams]);

  // Live discussion thread while the drawer is open (the task's standalone
  // discussion: discussions/task__{taskId}).
  useEffect(() => {
    if (!threadId) {
      setNotes([]);
      return undefined;
    }
    setNotesLoading(true);
    const unsub = subscribeDiscussionMessages(threadId, (list) => {
      setNotes(list);
      setNotesLoading(false);
    });
    return unsub;
  }, [threadId]);

  // Mark the task thread read on OPEN and on CLOSE (threadId → null) — not per
  // incoming message, which would write to discussion_reads on every snapshot.
  // Stamp the last note's createdAt (ref) so the marker compares cleanly against
  // lastMessageAt without client-clock skew.
  const lastNoteAtRef = useRef(null);
  useEffect(() => {
    lastNoteAtRef.current = notes.length
      ? notes[notes.length - 1]?.createdAt || null
      : null;
  }, [notes]);
  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!threadId || !uid) return undefined;
    markThreadRead(uid, threadId, lastNoteAtRef.current);
    return () => markThreadRead(uid, threadId, lastNoteAtRef.current);
  }, [threadId]);

  // Populate the edit form whenever a task is opened (runs once the modal —
  // and therefore the Form — is mounted, so setFieldsValue always lands).
  useEffect(() => {
    if (editTask) {
      editForm.setFieldsValue({
        title: editTask.title || "",
        description: editTask.description || "",
        deadline: editTask.deadline ? dayjs(editTask.deadline) : null,
        assignedTo: Array.isArray(editTask.assignedTo)
          ? editTask.assignedTo
          : editTask.assignTo
            ? [editTask.assignTo]
            : [],
        vendorId: editTask.vendorId || undefined,
      });
    }
  }, [editTask, editForm]);

  const load = async () => {
    setLoading(true);
    try {
      const [taskSnap, propSnap, userSnap, vendorSnap] = await Promise.all([
        getDocs(collection(db, COLLECTION)),
        getDocs(collection(db, "properties")),
        getDocs(collection(db, "users")),
        getDocs(collection(db, "vendors")),
      ]);
      setTasks(taskSnap.docs.map((d) => ({ _id: d.id, ...d.data() })));
      setProperties(propSnap.docs.map((d) => ({ _id: d.id, ...d.data() })));
      setInspectors(
        userSnap.docs
          .map((d) => ({ _id: d.id, ...d.data() }))
          .filter(isAssignableInspector),
      );
      setVendors(vendorSnap.docs.map((d) => ({ _id: d.id, ...d.data() })));
    } catch (e) {
      console.error("load tasks failed", e);
      message.error("Couldn't load tasks");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Logged-in admin's display name, stamped on tasks they create.
  const currentName = getDisplayName(adminUser);

  // Map a list of inspector uids -> their display names.
  const namesFor = (ids) => taskAssigneeNames(ids, inspectors);

  // Resolve a vendor id -> its name (denormalized onto the task so it survives a
  // vendor rename/delete). Empty id -> no linked vendor.
  const vendorNameFor = (id) =>
    id ? vendors.find((v) => v._id === id)?.name || "" : "";

  const createTask = async (values) => {
    setSaving(true);
    try {
      const prop = properties.find((p) => p._id === values.propertyId);
      await createPropertyTask({
        title: values.title,
        description: values.description,
        propertyId: values.propertyId,
        propertyName: prop?.propertyName,
        deadline: values.deadline,
        assignedTo: values.assignedTo,
        users: inspectors,
        createdByName: currentName,
        vendorId: values.vendorId || null,
        vendorName: vendorNameFor(values.vendorId),
      });
      message.success("Task created");
      setModalOpen(false);
      form.resetFields();
      load();
    } catch (e) {
      console.error("create task failed", e);
      message.error("Couldn't create task");
    } finally {
      setSaving(false);
    }
  };

  // Edit an existing task's core fields. Re-stamps assignedToNames from the
  // current uids and records updatedBy/updatedAt — the latter lets the
  // notify-on-update function skip the editing admin and avoid self-pings.
  const updateTask = async (values) => {
    if (!editTask) return;
    setSavingEdit(true);
    try {
      const ids = values.assignedTo || [];
      // Re-resolve the vendor name from the live directory (picks up renames),
      // but if the id is unchanged and no longer resolvable (the vendor was
      // deleted), keep the stored name rather than wiping it to "" — the
      // denormalized name is meant to survive a vendor delete.
      const nextVendorId = values.vendorId || null;
      const nextVendorName = nextVendorId
        ? vendorNameFor(nextVendorId) ||
          (nextVendorId === editTask.vendorId ? editTask.vendorName || null : null)
        : null;
      await updateDoc(doc(db, COLLECTION, editTask._id), {
        title: values.title.trim(),
        description: values.description?.trim() || "",
        deadline: values.deadline ? values.deadline.toISOString() : null,
        assignedTo: ids,
        assignedToNames: namesFor(ids),
        vendorId: nextVendorId,
        vendorName: nextVendorName,
        updatedAt: new Date().toISOString(),
        updatedBy: auth.currentUser?.uid || null,
        updatedByName:
          auth.currentUser?.displayName || auth.currentUser?.email || "Admin",
      });
      message.success("Task updated");
      setEditTask(null);
      load();
    } catch (e) {
      console.error("update task failed", e);
      message.error("Couldn't update task");
    } finally {
      setSavingEdit(false);
    }
  };

  const setStatus = async (task, done) => {
    try {
      await updateDoc(doc(db, COLLECTION, task._id), {
        status: done ? "done" : "open",
        completedAt: done ? new Date().toISOString() : null,
        completedBy: done ? auth.currentUser?.uid || null : null,
        // Denormalized name so lists can show who completed it (matches mobile).
        completedByName: done ? currentName || "" : "",
      });
      load();
    } catch (e) {
      console.error("update task failed", e);
      message.error("Couldn't update task");
    }
  };

  const setReimbursed = async (task, reimbursed) => {
    try {
      await updateDoc(doc(db, COLLECTION, task._id), {
        reimbursed: !!reimbursed,
        reimbursedAt: reimbursed ? new Date().toISOString() : null,
      });
      message.success(reimbursed ? "Marked reimbursed" : "Reimbursement undone");
      load();
    } catch (e) {
      console.error("update reimbursement failed", e);
      message.error("Couldn't update reimbursement");
    }
  };

  const removeTask = (task) => {
    Modal.confirm({
      title: "Delete this task?",
      content: task?.title
        ? `"${task.title}" will be permanently deleted.`
        : "This task will be permanently deleted.",
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await deleteDoc(doc(db, COLLECTION, task._id));
          message.success("Task deleted");
          load();
        } catch (e) {
          console.error("delete task failed", e);
          message.error("Couldn't delete task");
        }
      },
    });
  };

  // needsAssignment / hasOpenQuestion are shared predicates (helper/propertyTasks)
  // so the dashboard count and the sidebar badge stay in lockstep with these.
  const needsCount = useMemo(
    () => tasks.filter(needsAssignment).length,
    [tasks],
  );
  const questionCount = useMemo(
    () => tasks.filter(hasOpenQuestion).length,
    [tasks],
  );

  // Reserved photo-slot count (staged + uploads in flight). A ref so the cap is
  // enforced SYNCHRONOUSLY — antd <Upload multiple> fires beforeUpload once per
  // file near-simultaneously, and reading replyImages.length (async state) would
  // be stale and let a single multi-pick blow past 6.
  const stagedCountRef = useRef(0);

  const openThread = async (task) => {
    setReplyText("");
    setReplyImages([]);
    stagedCountRef.current = 0;
    setThreadDirty(false);
    setThreadTask(task);
    setThreadId(null);
    try {
      // Ensure the thread exists before the messages subscription attaches.
      const disc = await getOrCreateDiscussion({
        subjectType: "task",
        subjectId: task._id,
        title: task.title,
        propertyId: task.propertyId || null,
        participants: [
          task.createdBy,
          ...(Array.isArray(task.assignedTo)
            ? task.assignedTo
            : task.assignTo
              ? [task.assignTo]
              : []),
        ].filter(Boolean),
        creatorName: currentName,
      });
      setThreadId(disc.docId);
    } catch (e) {
      console.error("openThread failed", e);
      message.error("Couldn't open discussion");
    }
  };
  const closeThread = () => {
    setThreadTask(null);
    setThreadId(null);
    noteEdit.reset(); // drop any in-progress edit so it can't leak to the next thread
    if (threadDirty) load(); // refresh so the question flag/tag/count update
  };

  // Upload one picked image to Storage (task-notes/) and stage its URL. Returns
  // false to stop antd's own upload.
  const handleAttach = async (file) => {
    if (stagedCountRef.current >= 6) {
      message.warning("Up to 6 photos per message");
      return false;
    }
    stagedCountRef.current += 1; // reserve a slot before the async upload
    setUploadingImg(true);
    try {
      const url = await uploadFile(file, "discussions");
      if (url) setReplyImages((prev) => [...prev, url]);
      else {
        stagedCountRef.current -= 1;
        message.error("Photo upload failed");
      }
    } catch (e) {
      stagedCountRef.current -= 1;
      message.error("Photo upload failed");
    } finally {
      setUploadingImg(false);
    }
    return false;
  };

  // Admin replies are always plain notes (the server notifies the assignee
  // either way); there's no admin "ask a question" path.
  const postReply = async () => {
    if (!threadTask || !threadId) return;
    if (!replyText.trim() && replyImages.length === 0) return;
    setPosting(true);
    try {
      await addDiscussionMessage(threadId, {
        text: replyText,
        type: "note",
        authorName: currentName,
        imageUrls: replyImages,
      });
      setReplyText("");
      setReplyImages([]);
      stagedCountRef.current = 0;
      setThreadDirty(true); // the realtime sub appends; flag clears server-side
    } catch (e) {
      console.error("post note failed", e);
      message.error(e?.message || "Couldn't post");
    } finally {
      setPosting(false);
    }
  };

  const filtered = useMemo(() => {
    let list = filterProperty
      ? tasks.filter((t) => t.propertyId === filterProperty)
      : tasks;
    if (needsOnly) list = list.filter(needsAssignment);
    if (questionsOnly) list = list.filter(hasOpenQuestion);
    if (taskFilter) list = list.filter((t) => t._id === taskFilter);
    // open first, then open-questions float to the top, then soonest deadline,
    // then newest.
    return [...list].sort((a, b) => {
      if ((a.status === "done") !== (b.status === "done"))
        return a.status === "done" ? 1 : -1;
      const aq = hasOpenQuestion(a) ? 0 : 1;
      const bq = hasOpenQuestion(b) ? 0 : 1;
      if (aq !== bq) return aq - bq;
      const ad = a.deadline ? dayjs(a.deadline).valueOf() : Infinity;
      const bd = b.deadline ? dayjs(b.deadline).valueOf() : Infinity;
      if (ad !== bd) return ad - bd;
      return dayjs(b.createdAt).valueOf() - dayjs(a.createdAt).valueOf();
    });
  }, [tasks, filterProperty, needsOnly, questionsOnly, taskFilter]);

  const columns = [
    {
      title: "Task",
      dataIndex: "title",
      sorter: (a, b) =>
        (a.title || "").toLowerCase().localeCompare((b.title || "").toLowerCase()),
      render: (t, r) => (
        <div>
          <div style={{ fontWeight: 600 }}>
            {unreadSet.has(subjectThreadId("task", r._id)) && (
              <span
                title="New messages"
                style={{
                  display: "inline-block",
                  width: 8,
                  height: 8,
                  borderRadius: 4,
                  background: "#c2603d",
                  marginRight: 6,
                  verticalAlign: "middle",
                }}
              />
            )}
            {t}
            {hasOpenQuestion(r) && (
              <Tag
                color="volcano"
                icon={<MessageOutlined />}
                style={{ marginLeft: 8, cursor: "pointer" }}
                onClick={() => openThread(r)}
              >
                Question
              </Tag>
            )}
          </div>
          {r.description ? (
            <div style={{ color: "#888", fontSize: 12 }}>{r.description}</div>
          ) : null}
        </div>
      ),
    },
    {
      title: "Property",
      dataIndex: "propertyName",
      sorter: (a, b) =>
        (a.propertyName || "")
          .toLowerCase()
          .localeCompare((b.propertyName || "").toLowerCase()),
      render: (v) => v || "—",
    },
    {
      title: "Deadline",
      dataIndex: "deadline",
      // No-deadline tasks sort last (Infinity), matching the default ordering.
      sorter: (a, b) =>
        (a.deadline ? dayjs(a.deadline).valueOf() : Infinity) -
        (b.deadline ? dayjs(b.deadline).valueOf() : Infinity),
      render: (v) => (v ? dayjs(v).format("MMM D, YYYY") : "—"),
    },
    {
      title: "Status",
      dataIndex: "status",
      // Open tasks (need attention) sort before done.
      sorter: (a, b) =>
        (a.status === "done" ? 1 : 0) - (b.status === "done" ? 1 : 0),
      render: (v, r) =>
        v === "done" ? (
          <Space direction="vertical" size={0}>
            <Tag color="green">Done</Tag>
            {!!r.completedByName && (
              <span style={{ fontSize: 12, color: "#888" }}>
                by {r.completedByName}
              </span>
            )}
          </Space>
        ) : (
          <Tag color="gold">Open</Tag>
        ),
    },
    {
      title: "Assigned to",
      render: (_, r) => {
        const names =
          Array.isArray(r.assignedToNames) && r.assignedToNames.length
            ? r.assignedToNames
            : r.assignToName
              ? [r.assignToName]
              : [];
        return (
          <Space size={4} wrap>
            {names.length ? (
              names.map((n, i) => (
                <Tag key={i} color="blue">
                  {n}
                </Tag>
              ))
            ) : (
              <span style={{ color: "#aaa" }}>Unassigned</span>
            )}
            <Button size="small" type="link" onClick={() => setEditTask(r)}>
              Edit
            </Button>
          </Space>
        );
      },
    },
    {
      title: "Vendor",
      dataIndex: "vendorName",
      render: (v) =>
        v ? <Tag color="purple">{v}</Tag> : <span style={{ color: "#aaa" }}>—</span>,
    },
    {
      title: "Created by",
      dataIndex: "createdByName",
      sorter: (a, b) =>
        (a.createdByName || "")
          .toLowerCase()
          .localeCompare((b.createdByName || "").toLowerCase()),
      render: (v) => v || "—",
    },
    {
      title: "Purchase / Receipt",
      render: (_, r) => {
        const hasCost = r.cost != null && r.cost !== "";
        const receipts = Array.isArray(r.receiptImages) ? r.receiptImages : [];
        if (r.status !== "done" || (!hasCost && receipts.length === 0)) {
          return <span style={{ color: "#bbb" }}>—</span>;
        }
        return (
          <div>
            {hasCost && (
              <div style={{ fontWeight: 600 }}>${Number(r.cost).toFixed(2)}</div>
            )}
            {r.resolutionNote ? (
              <div style={{ color: "#888", fontSize: 12 }}>{r.resolutionNote}</div>
            ) : null}
            {receipts.length > 0 && (
              <Space size={4} wrap style={{ marginTop: 4 }}>
                {receipts.map((url, i) => (
                  <a key={i} href={url} target="_blank" rel="noreferrer">
                    <img
                      src={url}
                      alt={`receipt ${i + 1}`}
                      style={{
                        width: 36,
                        height: 36,
                        objectFit: "cover",
                        borderRadius: 4,
                        border: "1px solid #eee",
                      }}
                    />
                  </a>
                ))}
              </Space>
            )}
            <div style={{ marginTop: 6 }}>
              {r.reimbursed ? (
                <Tag color="green">Reimbursed</Tag>
              ) : (
                <Tag color="gold">Awaiting</Tag>
              )}
              <Button
                size="small"
                type="link"
                onClick={() => setReimbursed(r, !r.reimbursed)}
              >
                {r.reimbursed ? "Undo" : "Mark reimbursed"}
              </Button>
            </div>
          </div>
        );
      },
    },
    {
      title: "Actions",
      render: (_, r) => (
        <Dropdown
          trigger={["click"]}
          menu={{
            items: [
              {
                key: "discussion",
                label: "View discussion",
                onClick: () => openThread(r),
              },
              {
                key: "edit",
                label: "Edit",
                onClick: () => setEditTask(r),
              },
              {
                key: "status",
                label: r.status === "done" ? "Reopen" : "Mark done",
                onClick: () => setStatus(r, r.status !== "done"),
              },
              { type: "divider" },
              {
                key: "delete",
                label: "Delete",
                danger: true,
                onClick: () => removeTask(r),
              },
            ],
          }}
        >
          <Button type="text" icon={<MoreOutlined />} />
        </Dropdown>
      ),
    },
  ];

  return (
    <div className="admin-products-main-container">
      <PageHeader
        eyebrow="Work"
        title="Property Tasks"
        subtitle="Ad-hoc maintenance to-dos tied to a property — assign them, track questions, and reimburse costs."
        actions={
          <Space wrap>
            <Select
              allowClear
              placeholder="Filter by property"
              style={{ minWidth: 220 }}
              value={filterProperty}
              onChange={setFilterProperty}
              options={properties.map((p) => ({
                value: p._id,
                label: p.propertyName,
              }))}
            />
            <Button
              type={questionsOnly ? "primary" : "default"}
              danger={!questionsOnly && questionCount > 0}
              icon={<MessageOutlined />}
              onClick={() => setQuestionsOnly((v) => !v)}
            >
              Questions{questionCount ? ` (${questionCount})` : ""}
            </Button>
            <Button
              type={needsOnly ? "primary" : "default"}
              danger={!needsOnly && needsCount > 0}
              onClick={() => setNeedsOnly((v) => !v)}
            >
              Needs assignment{needsCount ? ` (${needsCount})` : ""}
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setModalOpen(true)}
            >
              New Task
            </Button>
          </Space>
        }
      />

      {taskFilter && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            margin: "0 0 12px",
            padding: "8px 12px",
            background: "#fff7e6",
            border: "1px solid #ffd591",
            borderRadius: 6,
          }}
        >
          <span>
            {filtered.length === 0
              ? "That task isn't in the current view — it may be completed or hidden by another filter."
              : "Showing one task (linked from a discussion)."}
          </span>
          <Button
            type="link"
            size="small"
            style={{ padding: 0, height: "auto" }}
            onClick={() => {
              // Clear the deep-link filter AND any other active filters so the
              // linked task is guaranteed to be visible.
              searchParams.delete("task");
              setSearchParams(searchParams, { replace: true });
              setNeedsOnly(false);
              setQuestionsOnly(false);
              setFilterProperty(null);
            }}
          >
            Show all tasks
          </Button>
        </div>
      )}

      <div className="server-roles-tb-main-container">
        <Table
          rowKey="_id"
          className="subscriptionapi-table"
          loading={loading}
          columns={columns}
          dataSource={filtered}
          pagination={{ pageSize: 10 }}
          rowClassName={(record, index) =>
            index % 2 === 0 ? "server-role-even-row" : "server-role-odd-row"
          }
        />
      </div>

      <Modal
        title="New Task"
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={saving}
        okText="Create"
      >
        <Form form={form} layout="vertical" onFinish={createTask}>
          <Form.Item
            name="title"
            label="Title"
            rules={[{ required: true, message: "Give the task a title" }]}
          >
            <Input placeholder="e.g. Refill water softener salt" />
          </Form.Item>
          <Form.Item name="description" label="Details (optional)">
            <Input.TextArea rows={3} placeholder="Anything the inspector should know" />
          </Form.Item>
          <Form.Item
            name="propertyId"
            label="Property"
            rules={[{ required: true, message: "Pick a property" }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="Choose a property"
              options={properties.map((p) => ({
                value: p._id,
                label: p.propertyName,
              }))}
            />
          </Form.Item>
          <Form.Item
            name="assignedTo"
            label="Assign to (optional)"
            extra="Pick one or more inspectors. Leave blank and the admins are notified to assign it."
          >
            <Select
              mode="multiple"
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="Assign to inspectors"
              options={inspectors.map((u) => ({
                value: u._id,
                label: getUserLabel(u),
              }))}
            />
          </Form.Item>
          <Form.Item
            name="vendorId"
            label="Vendor (optional)"
            extra="Link a vendor from the directory if this work is handled by one."
          >
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="Choose a vendor"
              options={vendors.map((v) => ({ value: v._id, label: v.name }))}
            />
          </Form.Item>
          <Form.Item name="deadline" label="Deadline (optional)">
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Edit task"
        open={!!editTask}
        onCancel={() => setEditTask(null)}
        onOk={() => editForm.submit()}
        confirmLoading={savingEdit}
        okText="Save"
        destroyOnClose
      >
        <p style={{ color: "#888", marginTop: 0 }}>
          {editTask?.propertyName || "—"}
        </p>
        <Form form={editForm} layout="vertical" onFinish={updateTask}>
          <Form.Item
            name="title"
            label="Title"
            rules={[{ required: true, message: "Give the task a title" }]}
          >
            <Input placeholder="e.g. Refill water softener salt" />
          </Form.Item>
          <Form.Item name="description" label="Details (optional)">
            <Input.TextArea
              rows={3}
              placeholder="Anything the inspector should know"
            />
          </Form.Item>
          <Form.Item
            name="assignedTo"
            label="Assign to (optional)"
            extra="Pick one or more inspectors. Leave blank and the admins are notified to assign it."
          >
            <Select
              mode="multiple"
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="Assign to inspectors"
              options={inspectors.map((u) => ({
                value: u._id,
                label: getUserLabel(u),
              }))}
            />
          </Form.Item>
          <Form.Item
            name="vendorId"
            label="Vendor (optional)"
            extra="Link a vendor from the directory if this work is handled by one."
          >
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="Choose a vendor"
              options={vendors.map((v) => ({ value: v._id, label: v.name }))}
            />
          </Form.Item>
          <Form.Item name="deadline" label="Deadline (optional)">
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>
        </Form>
      </Modal>

      <Drawer
        title={threadTask ? `Discussion — ${threadTask.title}` : "Discussion"}
        open={!!threadTask}
        onClose={closeThread}
        width={460}
        styles={{ body: { display: "flex", flexDirection: "column" } }}
      >
        <p style={{ color: "#888", marginTop: 0 }}>
          {threadTask?.propertyName || "—"}
        </p>

        <div style={{ flex: 1, overflowY: "auto" }}>
          {notesLoading ? (
            <div style={{ textAlign: "center", padding: 24 }}>
              <Spin />
            </div>
          ) : notes.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No messages yet. Start the conversation."
            />
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {notes.map((n) => (
                <div
                  key={n._id}
                  style={{
                    borderLeft: `3px solid ${
                      n.type === "question" ? "#fa541c" : "#d9d9d9"
                    }`,
                    paddingLeft: 10,
                  }}
                >
                  <div
                    style={{
                      fontSize: 12,
                      color: "#888",
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <span>
                      {n.authorName || "Someone"}
                      {n.createdAt
                        ? ` · ${dayjs(n.createdAt).format("MMM D, h:mm A")}`
                        : ""}
                      {n.editedAt && !n.deleted ? " · edited" : ""}
                    </span>
                    {n.type === "question" && !n.deleted && (
                      <Tag color="volcano" style={{ marginLeft: 0 }}>
                        Question
                      </Tag>
                    )}
                    <MessageActionsMenu
                      n={n}
                      myUid={auth.currentUser?.uid}
                      onEdit={noteEdit.beginEdit}
                      onDelete={noteEdit.confirmDelete}
                      style={{ marginLeft: "auto" }}
                    />
                  </div>
                  <MessageBody
                    n={n}
                    editing={noteEdit.editingId === n._id}
                    editText={noteEdit.editText}
                    setEditText={noteEdit.setEditText}
                    savingEdit={noteEdit.savingEdit}
                    onSave={noteEdit.saveEdit}
                    onCancel={noteEdit.cancelEdit}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        <div
          style={{
            marginTop: 12,
            borderTop: "1px solid #f0f0f0",
            paddingTop: 12,
          }}
        >
          <Input.TextArea
            rows={3}
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder="Write a reply…"
          />
          {replyImages.length > 0 && (
            <Space wrap size={6} style={{ marginTop: 8 }}>
              {replyImages.map((url, i) => (
                <div key={i} style={{ position: "relative" }}>
                  <Image
                    src={url}
                    width={64}
                    height={64}
                    preview={false}
                    style={{ objectFit: "cover", borderRadius: 4 }}
                  />
                  <Button
                    size="small"
                    type="text"
                    icon={<CloseOutlined />}
                    onClick={() => {
                      setReplyImages((prev) => prev.filter((_, j) => j !== i));
                      stagedCountRef.current = Math.max(
                        0,
                        stagedCountRef.current - 1,
                      );
                    }}
                    style={{
                      position: "absolute",
                      top: -8,
                      right: -8,
                      background: "#fff",
                      borderRadius: "50%",
                      boxShadow: "0 0 2px rgba(0,0,0,.3)",
                    }}
                  />
                </div>
              ))}
            </Space>
          )}
          <Space style={{ marginTop: 8 }}>
            <Button
              type="primary"
              loading={posting}
              disabled={
                (!replyText.trim() && replyImages.length === 0) || uploadingImg
              }
              onClick={() => postReply()}
            >
              Reply
            </Button>
            <Upload
              accept="image/*"
              multiple
              showUploadList={false}
              beforeUpload={handleAttach}
            >
              <Button icon={<PictureOutlined />} loading={uploadingImg}>
                Add photo
              </Button>
            </Upload>
          </Space>
          <div style={{ fontSize: 12, color: "#aaa", marginTop: 6 }}>
            Your reply notifies the task&rsquo;s assignee(s) and marks any open
            question answered. Up to 6 photos.
          </div>
        </div>
      </Drawer>
    </div>
  );
};

export default PropertyTasks;
